/**
 * Utility to extract fields from SystemView tabs dynamically
 * 
 * This allows ExperienceView to pull fields from SystemView tabs
 * based on tab names, ensuring fields are correctly mapped.
 */

import type { TabStructure } from '@/features/inventory/item-detail/utils/buildTabsFromMeta';
import type { DocFieldSchema } from '@/lib/erp/meta';
import { buildTabsFromMeta } from '@/features/inventory/item-detail/utils/buildTabsFromMeta';
import type { DocTypeSchema } from '@/lib/erp/meta';

/**
 * Extract all fields from a SystemView tab by tab name
 * 
 * @param meta - DocType schema
 * @param tabName - Name of the tab (fieldname of Tab Break field, or "__details" for default)
 *                  Special markers: "__first_tab_after_details__", "__second_tab_after_details__" for index-based access
 * @returns Array of field schemas in that tab
 */
export function extractFieldsFromSystemTab(
  meta: DocTypeSchema,
  tabName: string
): DocFieldSchema[] {
  const systemTabs = buildTabsFromMeta(meta);
  
  let tab: TabStructure | undefined;
  
  // Handle special index-based markers for dynamic tab access
  if (tabName === '__first_tab_after_details__') {
    // Get first tab after "__details" (index 1, since index 0 is usually Details)
    // If there's only one tab (Details), return empty array (no additional tab)
    // Otherwise use index 1 (first tab after Details)
    if (systemTabs.length > 1) {
      tab = systemTabs[1];
    } else {
      // Only Details tab exists, return empty array
      return [];
    }
  } else if (tabName === '__second_tab_after_details__') {
    // Get second tab after "__details" (index 2)
    // If there are at least 3 tabs, use index 2
    // If there are 2 tabs, use index 1 (first tab after Details)
    // If there's only 1 tab, return empty array
    if (systemTabs.length > 2) {
      tab = systemTabs[2];
    } else if (systemTabs.length > 1) {
      tab = systemTabs[1];
    } else {
      // Only Details tab exists, return empty array
      return [];
    }
  } else {
  // Find the tab by name
    tab = systemTabs.find(t => t.name === tabName || t.label.toLowerCase() === tabName.toLowerCase());
  }
  
  if (!tab) {
    // Tab not found - return empty array
    return [];
  }
  
  // Extract all fields from all sections in the tab
  const fields: DocFieldSchema[] = [];
  
  for (const section of tab.sections) {
    for (const column of section.columns) {
      fields.push(...column.fields);
    }
  }
  
  return fields;
}

/**
 * Extract fields from multiple SystemView tabs
 * 
 * @param meta - DocType schema
 * @param tabNames - Array of tab names
 * @returns Map of tab name to fields array
 */
export function extractFieldsFromSystemTabs(
  meta: DocTypeSchema,
  tabNames: string[]
): Map<string, DocFieldSchema[]> {
  const result = new Map<string, DocFieldSchema[]>();
  
  for (const tabName of tabNames) {
    const fields = extractFieldsFromSystemTab(meta, tabName);
    result.set(tabName, fields);
  }
  
  return result;
}

/**
 * Get all SystemView tabs for a doctype
 * 
 * @param meta - DocType schema
 * @returns Array of tab structures
 */
export function getSystemViewTabs(meta: DocTypeSchema): TabStructure[] {
  return buildTabsFromMeta(meta);
}

/**
 * Find a SystemView tab by name or label (case-insensitive)
 * 
 * @param meta - DocType schema
 * @param searchName - Tab name or label to search for
 * @returns Tab structure or null
 */
export function findSystemViewTab(
  meta: DocTypeSchema,
  searchName: string
): TabStructure | null {
  const tabs = getSystemViewTabs(meta);
  
  const normalizedSearch = searchName.toLowerCase();
  
  return tabs.find(tab => 
    tab.name.toLowerCase() === normalizedSearch ||
    tab.label.toLowerCase() === normalizedSearch
  ) || null;
}






